import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { generateQuotePdf, generatePdfFilename } from '../utils/generateQuotePdf';
import { buildCommonPdfLabels } from '../utils/pdfHelpers';
import type { StructuredData } from '../api/types';
import { loadLogo } from '../lib/contractorSettings';
import type { ContractorSettings } from '../lib/contractorSettings';

interface DownloadPdfButtonProps {
  data: {
    structured_data: StructuredData;
    created_at: string;
    contractor: ContractorSettings;
    vatRate: number | null;
    markupPercent: number | null;
    quoteId?: string;
    paymentTermDays?: number;
    locale?: string;
    validityDays?: number;
  };
  className?: string;
}

export function DownloadPdfButton({ data, className }: Readonly<DownloadPdfButtonProps>) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'et' ? 'et-EE' : 'en-US';
  const vatRate = data.vatRate;

  const handleDownload = useCallback(() => {
    const logo = loadLogo();
    const blob = generateQuotePdf({
      ...data,
      logo,
      locale: data.locale || locale,
      labels: {
        notesHeading: t('pdf.notes'),
        validUntilLabel: t('pdf.validUntil'),
        quoteFor: t('pdf.quoteFor'),
        dateLabel: t('pdf.date'),
        refLabel: t('pdf.ref'),
        paymentTerms: data.paymentTermDays
          ? t('pdf.paymentTerms', { days: data.paymentTermDays })
          : '',
        ...buildCommonPdfLabels(t, vatRate),
      },
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = generatePdfFilename(data.structured_data.client_name, data.created_at);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [data, t, locale, vatRate]);

  return (
    <Button
      variant="outline"
      aria-label={t('pdf.download')}
      onClick={handleDownload}
      className={cn('touch-target', className)}
    >
      <FileDown className="mr-1 h-4 w-4" />
      {t('pdf.download')}
    </Button>
  );
}
